import React from 'react';
import './NGLViewer.scss'
import * as NGL from 'ngl';
import {withStyles} from '@material-ui/core/styles';
import NGLContext from "./NGLContext"
import PropTypes from 'prop-types';
import Paper from '@material-ui/core/Paper';
import Divider from '@material-ui/core/Divider';
import Collapse from '@material-ui/core/Collapse';
import BottomPanel from './BottomPanel/BottomPanel'
import NGLAppBar from './NGLAppBar/NGLAppBar'
import ViewPanel from "./ViewPanel/ViewPanel";
import {ColormakerRegistry} from "ngl"
import Typography from '@material-ui/core/Typography';


const styles = theme => ({
    root: {
        flexGrow: 1,
        overflow: 'hidden',
        position: 'relative',
    },
});

export const getPropValue = (nestedObj, pathArr) => {
    return pathArr.reduce((obj, key) =>
        (obj && obj[key] !== 'undefined') ? obj[key] : undefined, nestedObj);
};

class DefaultDict {
    constructor(defaultVal) {
        return new Proxy({}, {
            get: (target, name) => name in target ? target[name] : defaultVal
        })
    }
};

function defaultRepr(component, stage, itername) {
    if (component.type === 'structure') {
        component.addRepresentation('cartoon', {
            colorScheme: "sstruc",
            name: "Sec. structure"
        });
        if (itername != "IT 0" && itername != "REFINE") {
            component.addRepresentation('line', {
                colorScheme: 'occupancy',
                colorScale: 'RdYlGn',
                colorReverse: true,
                sele: "sidechainAttached and not hydrogen",
                name: "RMS NOE viol"
            });
        }
        component.addRepresentation('tube', {
            sele: "/0 and .CA",
            colorScheme: 'bfactor',
            colorReverse: true,
            scale: 0.01,
            radiusType: 'bfactor',
            name: "RMSF bundle"
        }).setVisibility(false);
    }
    // Handle window resizing
    window.addEventListener("resize", function (event) {
        stage.handleResize();
    }, false);
}

@withStyles(styles, {withTheme: true})
class NGLViewer extends React.Component {

    static propTypes = {
        classes: PropTypes.object.isRequired,
        metadata: PropTypes.string,
        itername: PropTypes.string,
    };

    constructor(props) {
        super(props);
        this.viewRef = React.createRef();
        this.state = {
            stage: undefined,
            error: undefined,
            loaded: false,
            collapsed: true,
            drawerOpened: true,
            SettingsMenu: null,
            bottomPanelExpanded: false,
            componentIndex: 0,
            representationIndex: new DefaultDict(0),
            componentFolds: {},
        };
        this.initStage = this.initStage.bind(this);
        this.loadNGLFile = this.loadNGLFile.bind(this);
        this.handleComponentFold = this.handleComponentFold.bind(this);
        this.handleComponentListFold = this.handleComponentListFold.bind(this);
    }

    initStage = async props => {
        if (!this.state.loaded) {
            try {
                await this.setState({
                    loaded: true,
                });
                const stage = new NGL.Stage(this.viewRef.current, {
                    backgroundColor: "white",
                    hoverTimeout: 150
                });
                console.log("Load file ", props.pdb);
                await stage.loadFile(props.pdb, {
                    //defaultRepresentation: true,
                    // name:
                    sele: '*'
                }).then(
                    (structureComponent) => {
                        this.setState({
                            stage: stage,
                            pdb: props.pdb
                        }, async () => {
                            // Update component list
                            await defaultRepr(structureComponent, stage, props.itername);
                            this.handleComponentListFold(false);
                        });
                    })
            } catch (error) {
                this.setState({
                    error
                });
            }
        }
    };

    componentDidMount() {
        // Load the main pdb file at the end
        return this.initStage(this.props)
    }

    loadNGLFile = async input => {
        this.state.stage.removeAllComponents();
        await this.state.stage.loadFile(input.pdb, {sele: '*'}).then((o) => {
            defaultRepr(o, this.state.stage, input.itername);
        });
        this.setState({
            loaded: true,
            pdb: input.pdb.toString(),
        });
    };

    componentDidUpdate(prevProps, prevState, snapshot) {
        // TODO: check if pdb is in complist instead
        if (prevProps.pdb !== this.props.pdb) {
            console.log("Load new pdb file " + this.props.pdb.toString());
            this.loadNGLFile(this.props);
        }
    }

    handleComponentFold = (compIndex) => {
        this.setState(prevState => {
            const flag = prevState.componentFolds[compIndex] ?
                !prevState.componentFolds[compIndex] : true;
            return {
                componentFolds: {
                    ...prevState.componentFolds,
                    [compIndex]: flag
                }
            }
        })
    };

    handleComponentListFold = (fold) => {
        if (this.state.stage) {
            this.state.stage.compList.forEach((value, index) => {
                this.setState(prevState => ({
                    componentFolds: {
                        ...prevState.componentFolds,
                        [index]: !fold
                    }
                }));
            });
        }
    };

    render() {
        const {classes, theme} = this.props;
        return (
            <NGLContext.Provider value={
                {
                    ...this.state,
                    viewRef: this.viewRef,
                    handleComponentFold: this.handleComponentFold,
                    handleComponentListFold: this.handleComponentListFold,
                    handleBottomPanel: () => this.setState(prevState => ({bottomPanelExpanded: !prevState.bottomPanelExpanded})),
                    handleSettings: event => this.setState({SettingsMenu: event.currentTarget}),
                    closeSettings: () => this.setState({SettingsMenu: null}),
                    handleRefresh: () => this.setState(() => ({loaded: true})),
                    handleCollapse: () => this.setState((prevState) => ({collapsed: !prevState.collapsed})),
                    handleFullScreen: () => {
                        this.state.stage.toggleFullscreen()
                    },
                    handleDrawer: () => this.setState((prevState) => ({drawerOpened: !prevState.drawerOpened})),
                    handleCompListItemClick: (index) => this.setState({componentIndex: index}),
                    handleReprListItemClick: (compIndex, reprIndex) => this.setState(prevState => ({
                        representationIndex: {
                            ...prevState.representationIndex,
                            [compIndex]: reprIndex
                        }
                    })),
                }
            }>
                <NGLContext.Consumer>
                    {(contextNGL) => (
                        <React.Fragment>

                            <Paper className={classes.root}>

                                <NGLAppBar stage={contextNGL.stage}/>

                                <Collapse in={contextNGL.collapsed}
                                          timeout="auto">
                                    <Divider/>
                                    <ViewPanel/>
                                    <Divider/>
                                    <BottomPanel/>
                                </Collapse>
                            </Paper>
                        </React.Fragment>
                    )}
                </NGLContext.Consumer>
            </NGLContext.Provider>
        );
    }
}

export default NGLViewer;